/* ============================================================
   DUPLICATE GROUP CARD
   ============================================================ */

import {
  Paper,
  Box,
  Typography,
  Chip,
  Collapse,
  Button,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import { useState } from 'react';
import { ContentRecord, DuplicateGroup, MatchReason } from './types';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import GroupOutlinedIcon from '@material-ui/icons/GroupOutlined';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CopyClipboard from '@material-ui/icons/AttachFile';
import ReplayIcon from '@material-ui/icons/Replay';
import {
  CellForContentGenres,
  CellForContentSource,
  originCountryNames,
  formatDate,
  formatGroupName,
  formatMatchReason,
} from './utils';
import { windowObj } from '@lib/common';
import { CellForContentType } from '@pages/content/search/columns/utils';
import styled from 'styled-components';

type DuplicateGroupCardMode = 'duplicate' | 'nonDuplicate';

interface DuplicateGroupCardProps {
  group: DuplicateGroup;
  expanded: boolean;
  onToggle: () => void;
  mode?: DuplicateGroupCardMode;

  onNotDuplicate?: (group: DuplicateGroup, selectedIds: string[]) => void;
  onMarkDuplicate?: (group: DuplicateGroup) => void;

  onMerge?: (group: DuplicateGroup, selectedIds: string[]) => void;
  actionLoading?: boolean;
  isMapped?: boolean;
}

export const DuplicateGroupCard: React.FC<DuplicateGroupCardProps> = ({
  group,
  expanded,
  onToggle,
  mode = 'duplicate',
  onNotDuplicate,
  onMarkDuplicate,
  onMerge,
  actionLoading = false,
  isMapped = false,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const isDuplicateMode = mode === 'duplicate';
  const actionsLocked = isMapped;

  /* ----------------------------------------------------------
     SELECT RECORD
     ---------------------------------------------------------- */

  const handleSelectRecord = (recordId: string) => {
    setSelectedIds(previous => {
      if (previous.includes(recordId)) {
        return previous.filter(id => id !== recordId);
      }

      return [...previous, recordId];
    });
  };

  /* ----------------------------------------------------------
     SELECT ALL
     ---------------------------------------------------------- */

  const handleSelectAll = () => {
    if (selectedIds.length === group.records.length) {
      setSelectedIds([]);

      return;
    }

    setSelectedIds(group.records.map(record => record.vtr_id));
  };

  const allSelected = group.records.length > 0 && selectedIds.length === group.records.length;

  /* ----------------------------------------------------------
     RENDER
     ---------------------------------------------------------- */

  return (
    <Paper
      elevation={0}
      style={{
        marginBottom: 7,
        border: '1px solid #D7E2F0',
        borderRadius: 7,
        overflow: 'hidden',
        background: '#FFFFFF',
      }}>
      {/* ====================================================
          GROUP HEADER
          ==================================================== */}

      <Box
        onClick={onToggle}
        style={{
          minHeight: 44,
          padding: '0 11px',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          background: expanded ? '#F8FBFF' : '#FFFFFF',
          borderBottom: expanded ? '1px solid #E5EBF3' : 'none',
        }}>
        {/* GROUP ICON */}

        <Box
          style={{
            width: 25,
            height: 25,
            borderRadius: 6,
            marginRight: 7,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#EEF5FF',
          }}>
          <GroupOutlinedIcon
            style={{
              fontSize: 15,
              color: '#2563EB',
            }}
          />
        </Box>

        {/* GROUP NAME */}

        <Typography
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#172033',
            whiteSpace: 'nowrap',
          }}>
          {formatGroupName(group.id)}
        </Typography>

        {/* SEPARATOR */}

        <Box
          style={{
            height: 18,
            width: 1,
            margin: '0 10px',
            background: '#D7DFEA',
          }}
        />

        {/* MATCH REASONS */}

        <MatchReasonChips reasons={group.matchReasons} />

        <Box
          style={{
            flex: 1,
          }}
        />

        {/* ITEM COUNT */}

        <Chip
          size="small"
          label={`${group.records.length} items`}
          style={{
            height: 22,
            fontSize: 9,
            fontWeight: 600,
            background: '#EEF5FF',
            color: '#2563EB',
          }}
        />

        {/* EXPAND ICON */}

        <ExpandMoreIcon
          style={{
            marginLeft: 4,
            fontSize: 19,
            color: '#64748B',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.18s ease',
          }}
        />
      </Box>

      {/* ====================================================
          COLLAPSIBLE CONTENT
          ==================================================== */}

      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Box>
          {/* ------------------------------------------------
              SELECT ALL BAR
              ------------------------------------------------ */}
        {isDuplicateMode && (

          <Box
            style={{
              height: 31,
              padding: '0 11px',
              display: 'flex',
              alignItems: 'center',
              background: '#FBFCFE',
              borderBottom: '1px solid #EDF1F5',
            }}>
            <input
              type="checkbox"
              checked={allSelected}
              disabled={actionsLocked}
              onChange={handleSelectAll}
              style={{
                width: 13,
                height: 13,
                marginRight: 7,
                accentColor: '#2563EB',
                cursor: actionsLocked ? 'not-allowed' : 'pointer',
              }}
            />

            <Typography
              style={{
                fontSize: 9,
                color: '#64748B',
              }}>
              Select all
            </Typography>

            <Box
              style={{
                flex: 1,
              }}
            />

            {selectedIds.length > 0 && (
              <Typography
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  color: '#2563EB',
                }}>
                {selectedIds.length} selected
              </Typography>
            )}
          </Box>
        )}
          {/* ------------------------------------------------
              RECORDS
              ------------------------------------------------ */}

          {group.records.map(record => (
            <DuplicateContentRow
              key={record.vtr_id}
              record={record}
              selectable={isDuplicateMode && !actionsLocked}
    selected={
      isDuplicateMode
        ? selectedIds.includes(record.vtr_id)
        : false
    }
    onSelect={
      isDuplicateMode
        ? () => handleSelectRecord(record.vtr_id)
        : undefined
    }/>
          ))}

          {/* ------------------------------------------------
              GROUP ACTION BAR
              ------------------------------------------------ */}

          <Box
            style={{
              minHeight: 40,
              padding: '5px 10px',
              display: 'flex',
              alignItems: 'center',
              background: '#FAFCFF',
              borderTop: '1px solid #E7EDF4',
            }}>
            <Box
              display="flex"
              alignItems="center"
              style={{
                flex: 1,
                minWidth: 0,
              }}>
              <Typography
                style={{
                  marginRight: 6,
                  fontSize: 11,
                  color: '#94A3B8',
                  whiteSpace: 'nowrap',
                }}>
                Matched on:
              </Typography>

              <MatchReasonChips reasons={group.matchReasons} />
              {/* NON-DUPLICATE METADATA */}
    {!isDuplicateMode && (
      <Box
        display="flex"
        alignItems="center"
        style={{
          marginLeft: 12,
          gap: 12,
          whiteSpace: 'nowrap',
        }}
      >
        <Typography
          style={{
            fontSize: 11,
            color: '#64748B',
          }}
        >
          <span style={{ color: '#94A3B8' }}>Marked by:</span>{' '}
          <strong>{group.createdBy || '—'}</strong>
        </Typography>

        <Typography
          style={{
            fontSize: 11,
            color: '#64748B',
          }}
        >
          <span style={{ color: '#94A3B8' }}>Marked at:</span>{' '}
          <strong>
            {group.createdAt
              ? formatDate(group.createdAt)
              : '—'}
          </strong>
        </Typography>
      </Box>
    )}
            </Box>

            {/* DUPLICATE MODE */}

{isDuplicateMode && (
<>
            <Button
              variant="outlined"
              size="small"
              disabled={actionLoading || actionsLocked}
              startIcon={
                actionLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <CheckOutlinedIcon />
                )
              }
              onClick={event => {
                event.stopPropagation();

                if (actionLoading || actionsLocked) {
                  return;
                }

                onNotDuplicate(group,selectedIds);
              }}
              style={{
                height: 28,
                minWidth: 112,
                marginLeft: 7,
                borderColor: actionsLocked ? '#CBD5E1' : '#22A06B',
                color: actionsLocked ? '#94A3B8' : '#16804D',
                textTransform: 'none',
                fontSize: 9,
                fontWeight: 600,
                borderRadius: 5,
              }}>
              {actionLoading ? 'Processing...' : 'Not Duplicate'}
            </Button>

            {/* MERGE */}

            <Button
              variant="outlined"
              size="small"
              disabled={actionLoading || actionsLocked}
              startIcon={actionsLocked ? <CheckOutlinedIcon /> : <GetAppOutlinedIcon />}
              onClick={event => {
                event.stopPropagation();

                if (actionsLocked) {
                  return;
                }

                onMerge(group, selectedIds);
              }}
              style={{
                height: 28,
                minWidth: 120,
                marginLeft: 7,
                borderColor: actionsLocked ? '#16804D' : '#2563EB',
                color: actionsLocked ? '#16804D' : '#2563EB',
                background: actionsLocked ? '#EAF8EF' : 'transparent',
                textTransform: 'none',
                fontSize: 9,
                fontWeight: 600,
                borderRadius: 5,
              }}>
              {actionsLocked ? 'Merged' : 'Merge Selected'}
            </Button>
            </>
)}
            {/* NON-DUPLICATE MODE */}
            {!isDuplicateMode && (
  <Button
    variant="outlined"
    size="small"
    disabled={actionLoading}
    onClick={event => {
      event.stopPropagation();

      if (actionLoading) {
        return;
      }

      onMarkDuplicate?.(group);
    }}
    startIcon={
      actionLoading ? (
        <CircularProgress size={12} color="inherit" />
      ) : (
        <ReplayIcon />
      )
    }
    style={{
      height: 28,
      minWidth: 118,
      marginLeft: 7,
      borderColor: '#2563EB',
      color: '#2563EB',
      textTransform: 'none',
      fontSize: 9,
      fontWeight: 600,
      borderRadius: 5,
    }}
  >
    {actionLoading ? 'Processing...' : 'Mark as Duplicate'}
  </Button>
)}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

/* ============================================================
   MATCH REASON CHIPS
   ============================================================ */

interface MatchReasonChipsProps {
  reasons: MatchReason[];
}

const MatchReasonChips: React.FC<MatchReasonChipsProps> = ({ reasons }) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      style={{
        gap: 4,
        flexWrap: 'wrap',
        minWidth: 0,
      }}>
      {reasons?.map(reason => (
        <Chip
          key={reason}
          label={formatMatchReason(reason)}
          size="small"
          style={{
            height: 20,
            fontSize: 10,
            fontWeight: 600,
            borderRadius: 5,
            background: getMatchReasonBackground(reason),
            color: getMatchReasonColor(reason),
          }}
        />
      ))}
    </Box>
  );
};

/* ============================================================
   MATCH REASON BACKGROUND
   ============================================================ */

const getMatchReasonBackground = (reason: MatchReason): string => {
  switch (reason) {
    case 'IMDB_MATCH':
      return '#EAF8EF';

    case 'TITLE_CONTENT_TYPE_MATCH':
      return '#FFF4E5';

    case 'AKA_MATCH':
      return '#F3EEFF';

    case 'ORIGINAL_TITLE_MATCH':
      return '#EEF4FF';

    case 'WORKING_TITLE_MATCH':
      return '#FFF0F6';

    case 'TITLE_MATCH':
    default:
      return '#F0E9FF';
  }
};

/* ============================================================
   MATCH REASON COLOR
   ============================================================ */

const getMatchReasonColor = (reason: MatchReason): string => {
  switch (reason) {
    case 'IMDB_MATCH':
      return '#15803D';

    case 'TITLE_CONTENT_TYPE_MATCH':
      return '#B45309';

    case 'AKA_MATCH':
      return '#6D28D9';

    case 'ORIGINAL_TITLE_MATCH':
      return '#1D4ED8';

    case 'WORKING_TITLE_MATCH':
      return '#BE185D';

    case 'TITLE_MATCH':
    default:
      return '#6D28D9';
  }
};

/* ============================================================
   DUPLICATE CONTENT ROW
   ============================================================ */

interface DuplicateContentRowProps {
  record: ContentRecord;
  selectable?: boolean;

  selected?: boolean;

  onSelect?: () => void;
}

const DuplicateContentRow: React.FC<DuplicateContentRowProps> = ({
  record,
  selectable = true,
  selected = false,
  onSelect,
}) => {

  const handleImdbClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!record.imdb_id) {
      return;
    }

    window.open(`https://www.imdb.com/title/${record.imdb_id}/`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      style={{
        padding: '7px 11px 6px',
        backgroundColor: selected ? '#F4F8FF' : '#FFFFFF',
        borderBottom: '1px solid #EDF1F5',
        transition: 'background 0.15s ease',
      }}>
      {/* ==================================================
          PRIMARY INFORMATION ROW
          ================================================== */}

      <Box
        display="flex"
        alignItems="flex-start"
        style={{
          minHeight: 42,
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }}>
        {/* ==================================================
            CHECKBOX
            ================================================== */}
{selectable && (
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          style={{
            width: 14,
            height: 14,
            marginRight: 8,
            flexShrink: 0,
            cursor: 'pointer',
            accentColor: '#2563EB',
          }}
        />
        )}
        {/* ==================================================
            TITLE + POSTER + COPY VTR ID
            ================================================== */}

        <Box
          style={{
            flex: '1.4 1 160px',
            minWidth: 140,
            marginRight: 8,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            overflow: 'hidden',
          }}>
          {/* POSTER */}

          <Avatar
            variant="square"
            alt={record.title || 'Content'}
            src={
              record.poster_path
                ? `${windowObj.RAZZLE_TMDB_IMAGE_URL}/${record.poster_path}`
                : `${windowObj.RAZZLE_S3_BUCKET_POSTER_URL}/${record.vtr_id}.png`
            }
            style={{
              width: 30,
              height: 40,
              flexShrink: 0,
              borderRadius: 3,
              backgroundColor: '#EEF2F7',
            }}
          />

          {/* TITLE */}

          <Box style={{ minWidth: 0, flex: 1 }}>
            <TitleHoverRow>
              <Typography
                component="a"
                href={`/content/edit/${record.vtr_id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontWeight: 700,
                  fontSize: 13,
                  color: '#2563EB',
                  lineHeight: 1.35,
                  textDecoration: 'none',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}>
                {record.title || record.original_title || '—'}
              </Typography>
              {record.vtr_id ? <CopyVtrIdButton vtrId={record.vtr_id} /> : null}
            </TitleHoverRow>
          </Box>
        </Box>

        {/* CONTENT TYPE */}

        <Box
          style={{
            width: 36,
            minWidth: 36,
            marginRight: 8,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
          }}>
          <CellForContentType value={record.content_type} />
        </Box>

        {/* GENRE */}

        <Box
          style={{
            flex: '1 1 140px',
            minWidth: 0,
            marginRight: 8,
            overflow: 'hidden',
          }}>
          <GenreDisplay genres={record.genres} />
        </Box>

        <MetaColumn label="Language" width={88}>
          <ValueChips
            background="#EFF6FF"
            color="#1D4ED8"
            values={(record.lang_names || '')
              .split(',')
              .map(value => value.trim())
              .filter(Boolean)}
          />
        </MetaColumn>

        <MetaColumn label="Origin Country" width={120}>
          <ValueChips
            background="#FFF7ED"
            color="#C2410C"
            values={originCountryNames(record.origin_country)
              .split(',')
              .map(value => value.trim())
              .filter(Boolean)}
          />
        </MetaColumn>

        {/* IMDb */}

        <Box
          style={{
            width: 96,
            minWidth: 96,
            marginRight: 8,
            flexShrink: 0,
            overflow: 'hidden',
          }}>
          <Typography
            style={{
              fontSize: 11,
              color: '#94A3B8',
              marginBottom: 1,
            }}>
            IMDb
          </Typography>

          {record.imdb_id ? (
            <Typography
              component="button"
              type="button"
              onClick={handleImdbClick}
              title={`Open ${record.imdb_id} in IMDb`}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                margin: 0,
                fontSize: 12,
                fontWeight: 600,
                color: '#2563EB',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}>
              {record.imdb_id}
            </Typography>
          ) : (
            <Typography
              style={{
                fontSize: 12,
                color: '#94A3B8',
              }}>
              —
            </Typography>
          )}
        </Box>

        {/* RELEASE */}

        <DateColumn label="Release Date" value={record.release_date} />

        {/* CREATED */}

        <DateColumn label="Created At" value={record.created_at} />

        {/* MODIFIED */}

        <DateColumn label="Modified At" value={record.modified_at} />

        {/* SOURCE */}

        <Box
          style={{
            width: 72,
            minWidth: 72,
            flexShrink: 0,
            textAlign: 'right',
          }}>
          <Typography
            style={{
              fontSize: 11,
              color: '#94A3B8',
              lineHeight: 1.2,
              marginBottom: 2,
            }}>
            Source
          </Typography>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
            <CellForContentSource value={record.datasource_id} />
          </Box>
        </Box>
      
      </Box>
        {/* ==================================================
          SECONDARY INFORMATION
          ================================================== */}

        <Box
          display="flex"
          alignItems="center"
          style={{
            paddingLeft: selectable ? 22 : 0,
            marginTop: 6,
            gap: 12,
            minHeight: 22,
            flexWrap: 'wrap',
          }}>
          <ContentMetadataItem
            label="Original"
            value={record.original_title}
            wrap
            color="#1D4ED8"
          />

          <MetadataDivider />

          <ContentMetadataItem
            label="AKA"
            value={record.content_aka}
            color="#6D28D9"
          />

          <MetadataDivider />

          <ContentMetadataItem
            label="W/T"
            value={record.working_title}
            color="#BE185D"
          />

          <MetadataDivider />

          <ContentMetadataItem
            label="Status"
            value={record.status || record.vtr_content_status}
            color="#0F766E"
          />
        </Box>
    </Box>
  );
};
interface GenreDisplayProps {
  genres?: string | null;
}

const GenreDisplay: React.FC<
  GenreDisplayProps
> = ({
  genres,
}) => {
  if (!genres) {
    return (
      <Box>
        <Typography
          style={{
            fontSize: 11,
            color: '#94A3B8',
            marginBottom: 2,
          }}
        >
          Genre
        </Typography>

        <Typography
          style={{
            fontSize: 12,
            color: '#CBD5E1',
          }}
        >
          —
        </Typography>
      </Box>
    );
  }

  /*
   * Support values such as:
   *
   * "Action,Adventure,Drama,Thriller"
   *
   * or
   *
   * "Action|Adventure|Drama"
   */

  const genreList = genres
    .split(/[,|]/)
    .map(
      (genre) =>
        genre.trim()
    )
    .filter(Boolean);

  return (
    <Box
      style={{
        minWidth: 0,
      }}
    >
      <Typography
        style={{
          fontSize: 11,
          color: '#94A3B8',
          marginBottom: 2,
        }}
      >
        Genre
      </Typography>

      <Box
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          alignItems: 'center',
        }}
      >
        {genreList.map((genre, index) => (
          <Chip
            key={`${genre}-${index}`}
            size="small"
            label={genre}
            title={genre}
            style={{
              height: 24,
              fontSize: 11,
              fontWeight: 600,
              background: '#ECFDF5',
              color: '#047857',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};
const ValueChips: React.FC<{
  values: string[];
  background: string;
  color: string;
}> = ({ values, background, color }) => {
  if (!values.length) {
    return (
      <Typography
        style={{
          fontSize: 12,
          color: '#CBD5E1',
        }}
      >
        —
      </Typography>
    );
  }

  return (
    <Box
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        alignItems: 'center',
      }}
    >
      {values.map((value, index) => (
        <Chip
          key={`${value}-${index}`}
          size="small"
          label={value}
          title={value}
          style={{
            height: 24,
            maxWidth: '100%',
            fontSize: 11,
            fontWeight: 600,
            background,
            color,
          }}
        />
      ))}
    </Box>
  );
};

/* ============================================================
   META COLUMN
   ============================================================ */

const MetaColumn: React.FC<{
  label: string;
  width?: number;
  children: React.ReactNode;
}> = ({ label, width = 105, children }) => {
  return (
    <Box
      style={{
        width,
        minWidth: 0,
        maxWidth: width,
        marginRight: 8,
        flex: `0 1 ${width}px`,
        overflow: 'hidden',
      }}
    >
      <Typography
        style={{
          fontSize: 11,
          color: '#94A3B8',
          lineHeight: 1.2,
          marginBottom: 2,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
};

/* ============================================================
   DATE COLUMN
   ============================================================ */

interface DateColumnProps {
  label: string;
  value?: string;
}

const DateColumn: React.FC<DateColumnProps> = ({ label, value }) => {
  return (
    <Box
      style={{
        width: 108,
        minWidth: 108,
        marginRight: 6,
        flexShrink: 0,
      }}>
      <Typography
        style={{
          fontSize: 11,
          color: '#94A3B8',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
        }}>
        {label}
      </Typography>

      <Typography
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: '#475569',
          marginTop: 2,
          whiteSpace: 'nowrap',
        }}>
        {formatDate(value)}
      </Typography>
    </Box>
  );
};

/* ============================================================
   CONTENT METADATA ITEM
   ============================================================ */

interface ContentMetadataItemProps {
  label: string;
  value?: string;
  wrap?: boolean;
  color?: string;
}

const MetadataDivider = () => (
  <Box
    style={{
      width: 1,
      height: 12,
      background: '#CBD5E1',
      flexShrink: 0,
    }}
  />
);

const TitleHoverRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;

  .copy-vtr-id {
    display: none;
    flex-shrink: 0;
  }

  &:hover .copy-vtr-id {
    display: inline-flex;
  }
`;

const CopyVtrIdButton = ({ vtrId }: { vtrId: string }) => {
  const [copied, setCopied] = useState(false);

  return (
    <Tooltip title={copied ? 'Copied!!' : 'Copy the Vitrina Id'}>
      <IconButton
        className="copy-vtr-id"
        size="small"
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          navigator.clipboard.writeText(vtrId);
          setCopied(true);
        }}
        onMouseLeave={() => setCopied(false)}
        style={{ padding: 2, flexShrink: 0 }}>
        <CopyClipboard style={{ fontSize: 16 }} color="primary" />
      </IconButton>
    </Tooltip>
  );
};

const ContentMetadataItem: React.FC<ContentMetadataItemProps> = ({
  label,
  value,
  wrap = false,
  color = '#475569',
}) => {
  return (
    <Box
      display="flex"
      alignItems="flex-start"
      style={{
        minWidth: 0,
        maxWidth: wrap ? 360 : 230,
        flex: '0 1 auto',
      }}>
      <Typography
        style={{
          marginRight: 4,
          fontSize: 12,
          fontWeight: 400,
          color,
          flexShrink: 0,
        }}>
        {label}:
      </Typography>

      <Typography
        title={value || ''}
        style={{
          fontSize: 12,
          fontWeight: 400,
          color: value ? color : '#94A3B8',
          overflow: wrap ? 'visible' : 'hidden',
          textOverflow: wrap ? 'clip' : 'ellipsis',
          whiteSpace: wrap ? 'normal' : 'nowrap',
          wordBreak: wrap ? 'break-word' : undefined,
        }}>
        {value || '—'}
      </Typography>
    </Box>
  );
};

